import React, { useEffect, useMemo, useState } from 'react';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import { RoamingResponseData } from '@services/types/Roaming/tableData';
import { useTranslation } from 'react-i18next';
import useSearchParams from '@hooks/useSearchParams';
import { Input, Select } from 'antd';
import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';

const { Search } = Input;

type TableWithContentProps = {
  isRoot: boolean;
};

const TableWithContent = ({ isRoot }: TableWithContentProps) => {
  const { t: translate } = useTranslation('roaming');

  const [documentValue, setDocumentValue] = useState('');
  const [productValue, setProductValue] = useState('');
  const [customerNameValue, setCustomerNameValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'msisdn',
      'operator',
      'cpfCnpj',
      'customerName',
      'segment',
      'product',
      'rangeRoaming',
    ],
  });

  const {
    product,
    operator,
    msisdn,
    customerName,
    page,
    linesPerPage,
    segment,
    cpfCnpj,
    rangeRoaming,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(() => {
    const allFilters = {
      product: product && `%${product.toUpperCase()}%`,
      operator,
      msisdn,
      customerName: customerName && `%${customerName.toUpperCase()}%`,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage || 20,
      segment,
      cpfCnpj: cpfCnpj && `%${cpfCnpj}%`,
      rangeRoaming,
    };

    return allFilters;
  }, [
    product,
    operator,
    msisdn,
    customerName,
    page,
    linesPerPage,
    segment,
    cpfCnpj,
    rangeRoaming,
  ]);

  const { data: roamingData } = useSwr<RoamingResponseData>(
    '/service-proxy/roaming',
    {
      params: filters,
    },
  );

  const columns = useMemo(() => {
    return [
      {
        title: translate('table.msisdn'),
        dataIndex: 'msisdn',
      },
      {
        title: translate('table.operator'),
        dataIndex: 'operator',
      },

      {
        title: translate('table.roaming-dates'),
        dataIndex: 'qtdDaysRoaming',
      },

      {
        title: translate('table.customer'),
        dataIndex: 'customerName',
      },

      {
        title: translate('table.document'),
        dataIndex: 'cpfCnpj',
      },
      {
        title: translate('table.segment'),
        dataIndex: 'segment',
      },
      {
        title: translate('table.product'),
        dataIndex: 'product',
      },
      {
        title: translate('table.roaming-range'),
        dataIndex: 'rangeRoaming',
      },
      {
        title: translate('table.lastUpdated'),
        dataIndex: 'lastUpdated',
      },
    ];
  }, [translate]);

  const tableFilters = useMemo(
    () => [
      {
        visible: isRoot,
        label: translate('table.customer'),
        element: (
          <Search
            onSearch={
              (value: string) => setSearchParams({ customerName: value })
              // eslint-disable-next-line react/jsx-curly-newline
            }
            onChange={({ target: { value } }) => setCustomerNameValue(value)}
            value={customerNameValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: translate('table.operator'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: 'TIM',
                value: 'TIM',
              },
              {
                label: 'VIVO',
                value: 'VIVO',
              },
              {
                label: 'CLARO',
                value: 'CLARO',
              },
            ]}
            style={{ width: '100%' }}
            value={operator}
            allowClear
            showSearch
            onChange={value => setSearchParams({ operator: value })}
          />
        ),
      },
      {
        visible: true,
        label: translate('table.document'),
        element: (
          <Search
            onSearch={(value: string) => setSearchParams({ cpfCnpj: value })}
            onChange={({ target: { value } }) => setDocumentValue(value)}
            value={documentValue}
            allowClear
          />
        ),
      },

      {
        visible: true,
        label: translate('table.segment'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: 'EMPRESARIAL',
                value: 'EMPRESARIAL',
              },
              {
                label: 'CORPORATIVO',
                value: 'CORPORATIVO',
              },
            ]}
            style={{ width: '100%' }}
            value={segment}
            allowClear
            showSearch
            onChange={value => setSearchParams({ segment: value })}
          />
        ),
      },
      {
        visible: true,
        label: translate('table.product'),
        element: (
          <Search
            onSearch={(value: string) => setSearchParams({ product: value })}
            onChange={({ target: { value } }) => setProductValue(value)}
            value={productValue}
            allowClear
          />
        ),
      },

      {
        visible: true,
        label: translate('table.roaming-range'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: '60 - 79',
                value: '60 - 79 (Dias)',
              },
              {
                label: '80 - 89',
                value: '80 - 89 (Dias)',
              },
              {
                label: '> 90',
                value: '> 90 (Dias)',
              },
            ]}
            style={{ width: '100%' }}
            value={rangeRoaming}
            allowClear
            showSearch
            onChange={value => setSearchParams({ rangeRoaming: value })}
          />
        ),
      },
    ],
    [
      translate,
      setSearchParams,
      documentValue,
      productValue,
      isRoot,
      customerNameValue,
      rangeRoaming,
      operator,
      segment,
    ],
  );

  useEffect(() => {
    setDocumentValue(cpfCnpj);
  }, [cpfCnpj]);

  useEffect(() => {
    setProductValue(product);
  }, [product]);

  useEffect(() => {
    setCustomerNameValue(customerName);
  }, [customerName]);

  return (
    <div style={{ marginTop: 16 }}>
      <Table
        data={roamingData?.content || []}
        columns={columns}
        loading={!roamingData}
        tableKey="roaming"
        title={translate('table.title')}
        onTableChange={setSearchParams}
        extraActions={
          <RequestReport
            items={[
              {
                role: rolesConstants.REQUEST_REPORT,
                params: filters,
                path: '/service-proxy/roaming/report',
                type: 'roaming',
                method: 'get',
              },
            ]}
          />
        }
        pagination={{ page, linesPerPage, total: roamingData?.totalElements }}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
      />
    </div>
  );
};

export default TableWithContent;
